import * as React from "react";
import { useTranslation } from "react-i18next";

import { Box, Button, Dialog, IconButton, Typography } from "@mui/material";
import axios from "axios";

import useSnackbarAlert from "../../../utils/snackbarHandlers";
import { COLORS } from "./constants";
import deleteIcon from "./assets/delete-icon.png";

interface BulkConfirmModalProps {
  open: boolean;
  onClose: () => void;
  apiBaseURL: string;
  shopId: any;
  onConfirmed: () => void;
}

// A row that has been added to the list (game master details resolved up front so
// the list can show Name / Ticket Value / Pack Size before anything is confirmed)
interface BulkRow {
  gameNumber: string;
  packNumber: string;
  lotteryName: string;
  gameAmount: string;
  packSize: string;
}

// Bulk confirm popup (Figma node 2033:88 "Confirm Games User Inventories").
// The user scans or types a game + pack, hits Enter, and the ticket is appended to
// an in-memory list. Confirm then commits the whole list in one request and the
// tickets show up on the main Confirmed Tickets grid.
const BulkConfirmModal: React.FC<BulkConfirmModalProps> = ({ open, onClose, apiBaseURL, shopId, onConfirmed }) => {
  //@ts-ignore
  const config = window.config;
  const currencySymbol = config?.currencySymbol ?? "$";
  const { t } = useTranslation("translation");
  const { showErrorMessage, showSuccessMessage, showInfoMessage } = useSnackbarAlert();

  const [scanCode, setScanCode] = React.useState<string>("");
  const [gameNumber, setGameNumber] = React.useState<string>("");
  const [packNumber, setPackNumber] = React.useState<string>("");

  // put the cursor in the scan field whenever the popup opens, so a scanner can
  // fire straight into it without the user clicking first
  const scanRef = React.useRef<HTMLInputElement | null>(null);
  React.useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => scanRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [open]);
  const [rows, setRows] = React.useState<BulkRow[]>([]);
  const [adding, setAdding] = React.useState<boolean>(false);
  const [submitting, setSubmitting] = React.useState<boolean>(false);

  // Fresh list every time the popup opens
  React.useEffect(() => {
    if (open) {
      setRows([]);
      setScanCode("");
      setGameNumber("");
      setPackNumber("");
    }
  }, [open]);

  // Pack size is derived from the game price via config.numberOfPackMapping
  // (keys are plain prices, e.g. "5"); normalise "5.00" -> "5" before the lookup.
  const packSizeFor = (amount: string): string => {
    const key = String(parseFloat(amount));
    return config?.numberOfPackMapping?.[key] ?? "-";
  };

  // Validate, look up the game master, and append the ticket to the list.
  // fromScan clears the scan field regardless of outcome so the scanner is ready
  // for the next ticket; manual entry only clears on success.
  const addEntry = async (game: string, pack: string, fromScan: boolean) => {
    if (fromScan) setScanCode("");
    if (!/^\d{4}$/.test(game)) {
      return showErrorMessage(t("gameNumberFourDigitsError", "The game number must be 4 digits."));
    }
    if (!/^\d{7}$/.test(pack)) {
      return showErrorMessage(t("packNumberSevenDigitsError", "The pack number should be 7 digits."));
    }
    if (rows.some((r) => r.gameNumber === game && r.packNumber === pack)) {
      return showInfoMessage(t("bulkDuplicateInListMsg", "This ticket is already in the list"));
    }

    setAdding(true);
    try {
      const res: any = await axios.post(`${apiBaseURL}/api/inventory-v2/gameDetails`, { gameNumber: game });
      if (!res?.data?.found) {
        return showErrorMessage(t("gameNumberNotFound", "Game number not found"));
      }
      const gameAmount = String(res.data.gameAmount ?? "");
      setRows((prev) => [
        ...prev,
        {
          gameNumber: game,
          packNumber: pack,
          lotteryName: res.data.lotteryName ?? "",
          gameAmount,
          packSize: packSizeFor(gameAmount),
        },
      ]);
      setGameNumber("");
      setPackNumber("");
    } catch (error: any) {
      showErrorMessage(error?.response?.data?.message ?? t("somethingWentWrong", "Something went wrong"));
    } finally {
      setAdding(false);
    }
  };

  // Guards against handling the same scanned ticket twice: a barcode scanner
  // fires a burst of key events AND usually an Enter terminator, so both the
  // length trigger below and the Enter handler can fire for one physical scan.
  const processedScanRef = React.useRef<string>("");

  // Reuse the documented barcode split: game = first 4 chars, pack = next 7.
  const processScan = (code: string) => {
    const game = code.substring(0, 4);
    const pack = code.substring(4, 11);
    const key = `${game}-${pack}`;
    if (key === processedScanRef.current) return;
    processedScanRef.current = key;
    addEntry(game, pack, true);
  };

  // A completed scan (full 14-char code) auto-adds the ticket to the list.
  const onScanChange = (value: string) => {
    setScanCode(value);
    if (value.length < 14) processedScanRef.current = "";
    if (value.length >= 14) processScan(value);
  };

  // Barcode scanners end the code with an Enter key; also lets a user paste a
  // code and press Enter. Manual game/pack entry uses onEntryKeyDown below.
  const onScanKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const code = scanCode.trim();
    if (code.length >= 11) processScan(code);
  };

  const handleEnter = () => addEntry(gameNumber, packNumber, false);

  // Enter key on either manual field adds the ticket to the list.
  const onEntryKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (!adding) handleEnter();
  };

  const removeRow = (index: number) => setRows((prev) => prev.filter((_, i) => i !== index));

  const handleConfirm = () => {
    if (rows.length === 0) {
      return showErrorMessage(t("bulkNoRowsError", "Please add at least one ticket"));
    }
    setSubmitting(true);
    axios
      .post(`${apiBaseURL}/api/inventory-v2/bulkConfirm`, {
        userId: shopId,
        items: rows.map((r) => ({ gameNumber: r.gameNumber, packNumber: r.packNumber })),
      })
      .then((res: any) => {
        const { addedCount = 0, skippedDuplicatesCount = 0, invalidGameNumbersCount = 0 } = res?.data ?? {};
        if (addedCount > 0) {
          showSuccessMessage(t("bulkAddedMsg", `${addedCount} ticket(s) confirmed`));
          onConfirmed();
        }
        if (skippedDuplicatesCount > 0 || invalidGameNumbersCount > 0) {
          showInfoMessage(
            t(
              "bulkSkippedMsg",
              `Skipped ${skippedDuplicatesCount} duplicate(s) and ${invalidGameNumbersCount} invalid game number(s)`,
            ),
          );
        }
        onClose();
      })
      .catch((error: any) =>
        showErrorMessage(error?.response?.data?.message ?? t("somethingWentWrong", "Something went wrong")),
      )
      .finally(() => setSubmitting(false));
  };

  const labelSx = { fontSize: "17px", color: "#434343", mb: "6px", fontWeight: 600 } as const;
  const fieldSx = {
    height: "40px",
    borderRadius: "8px",
    border: `1px solid ${COLORS.border}`,
    backgroundColor: "#fff",
    px: "12px",
    outline: "none",
    fontSize: "17px",
  } as const;

  // Shared column layout so the header labels line up with each row's cells.
  const gridCols = "2.2fr 1.2fr 1.3fr 1fr 1.3fr";
  const rightCol = "120px"; // holds the Total badge (header) / delete icon (rows)
  // Cells are centre-aligned to match the Figma table (node 2033:88)
  const cellSx = { fontSize: "16px", color: "#434343", textAlign: "center" } as const;
  const headerCellSx = { fontSize: "17px", fontWeight: 700, color: "#000", textAlign: "center" } as const;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "880px",
          maxWidth: "96vw",
          borderRadius: "10px",
          border: `2px solid ${COLORS.orange}`,
          backgroundColor: COLORS.panelBg,
          overflow: "hidden",
        },
      }}
    >
      {/* Orange header */}
      <Box sx={{ backgroundColor: COLORS.orange, height: "48px", display: "flex", alignItems: "center", justifyContent: "space-between", px: 2 }}>
        <Typography sx={{ color: "#fff", fontSize: "22px", fontWeight: 700, letterSpacing: "1px" }}>
          {t("bulkConfirmTitle", "Confirm Games User Inventories")}
        </Typography>
        <Box component='span' onClick={onClose} sx={{ color: "#fff", fontSize: "26px", cursor: "pointer", lineHeight: 1 }} aria-label={t("close", "Close")}>
          ×
        </Box>
      </Box>

      <Box sx={{ p: 2.5 }}>
        {/* Entry bar: Scan Code + OR + Game Number + Pack Number + Enter */}
        <Box display='flex' alignItems='flex-end' gap='16px' flexWrap='wrap' mb={2}>
          <Box>
            <Typography sx={labelSx}>{t("scanCodeLabel", "Scan Code")}</Typography>
            <Box
              component='input'
              ref={scanRef}
              autoFocus
              value={scanCode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onScanChange(e.target.value)}
              onKeyDown={onScanKeyDown}
              placeholder={t("scanCodePlaceholder", "Scan / enter barcode")}
              sx={{ ...fieldSx, width: "260px" }}
            />
          </Box>

          <Typography sx={{ fontSize: "17px", color: "#434343", pb: "9px" }}>{t("or", "OR")}</Typography>

          <Box>
            <Typography sx={labelSx}>{t("gameNumberLabel", "Game Number")}</Typography>
            <Box
              component='input'
              value={gameNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGameNumber(e.target.value)}
              onKeyDown={onEntryKeyDown}
              sx={{ ...fieldSx, width: "130px" }}
            />
          </Box>

          <Box>
            <Typography sx={labelSx}>{t("packNumberLabel", "Pack Number")}</Typography>
            <Box
              component='input'
              value={packNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPackNumber(e.target.value)}
              onKeyDown={onEntryKeyDown}
              sx={{ ...fieldSx, width: "170px" }}
            />
          </Box>

          <Button
            onClick={handleEnter}
            disabled={adding}
            disableElevation
            sx={{
              backgroundColor: COLORS.blue,
              color: "#fff",
              textTransform: "none",
              borderRadius: "10px",
              height: "40px",
              px: 3,
              fontSize: "16px",
              fontWeight: 600,
              "&:hover": { backgroundColor: COLORS.blue, opacity: 0.9 },
            }}
          >
            {t("enterButton", "Enter")}
          </Button>
        </Box>

        {/* Orange divider separating entry from the list (Figma 2033:88) */}
        <Box sx={{ height: "2px", backgroundColor: COLORS.orange, mx: -2.5, mb: 1.5 }} />

        {/* List header: column labels + Total badge on the right */}
        <Box display='flex' alignItems='center' sx={{ px: "8px", pb: "6px" }}>
          <Box sx={{ flex: 1, display: "grid", gridTemplateColumns: gridCols, gap: "8px" }}>
            <Typography sx={headerCellSx}>{t("bulkColName", "Name")}</Typography>
            <Typography sx={headerCellSx}>{t("bulkColTicketValue", "Ticket Value")}</Typography>
            <Typography sx={headerCellSx}>{t("bulkColPackSize", "Pack Size #")}</Typography>
            <Typography sx={headerCellSx}>{t("bulkColGame", "Game #")}</Typography>
            <Typography sx={headerCellSx}>{t("bulkColPack", "Pack #")}</Typography>
          </Box>
          <Box sx={{ width: rightCol, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px" }}>
            <Typography sx={{ color: COLORS.orange, fontWeight: 700, fontSize: "17px" }}>{t("bulkTotalLabel", "Total :")}</Typography>
            <Box
              sx={{
                minWidth: "34px",
                height: "30px",
                px: "8px",
                borderRadius: "6px",
                backgroundColor: "#000",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: "16px",
              }}
            >
              {rows.length}
            </Box>
          </Box>
        </Box>

        {/* List rows (striped) */}
        <Box sx={{ maxHeight: "300px", overflowY: "auto" }}>
          {rows.length === 0 ? (
            <Typography sx={{ textAlign: "center", color: COLORS.grey, py: 3, fontSize: "16px" }}>
              {t("bulkEmptyList", "Scan or enter a ticket to add it to the list")}
            </Typography>
          ) : (
            rows.map((row, index) => (
              <Box
                key={`${row.gameNumber}-${row.packNumber}-${index}`}
                display='flex'
                alignItems='center'
                sx={{ px: "8px", height: "40px", backgroundColor: index % 2 === 0 ? COLORS.rowDark : COLORS.rowLight }}
              >
                <Box sx={{ flex: 1, display: "grid", gridTemplateColumns: gridCols, gap: "8px" }}>
                  <Typography sx={cellSx}>{row.lotteryName}</Typography>
                  <Typography sx={cellSx}>{`${currencySymbol}${row.gameAmount}`}</Typography>
                  <Typography sx={cellSx}>{row.packSize}</Typography>
                  <Typography sx={cellSx}>{row.gameNumber}</Typography>
                  <Typography sx={cellSx}>{row.packNumber}</Typography>
                </Box>
                <Box sx={{ width: rightCol, display: "flex", justifyContent: "flex-end" }}>
                  <IconButton size='small' onClick={() => removeRow(index)} aria-label={t("remove", "Remove")}>
                    <Box component='img' src={deleteIcon} alt={t("remove", "Remove")} sx={{ width: "26px", height: "27px" }} />
                  </IconButton>
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ borderTop: `1px solid ${COLORS.orange}`, display: "flex", justifyContent: "flex-end", gap: "12px", p: 2 }}>
        <Button
          onClick={onClose}
          disableElevation
          sx={{ backgroundColor: COLORS.navy, color: "#fff", textTransform: "none", borderRadius: "2px", px: 3, fontSize: "16px", fontWeight: 600, letterSpacing: "1px", "&:hover": { backgroundColor: COLORS.navy, opacity: 0.9 } }}
        >
          {t("close", "Close")}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={submitting}
          disableElevation
          sx={{ backgroundColor: COLORS.navy, color: "#fff", textTransform: "none", borderRadius: "2px", px: 3, fontSize: "16px", fontWeight: 600, letterSpacing: "1px", "&:hover": { backgroundColor: COLORS.navy, opacity: 0.9 } }}
        >
          {t("bulkConfirmSubmit", "Confirm")}
        </Button>
      </Box>
    </Dialog>
  );
};

export default BulkConfirmModal;
